import MerchantBenefits from "@/src/interfaces/benefits/merchant"
import Items from "../interfaces/catalog/items"
import CacheHandler from "../core/cache/cache-handler"

interface ItemProps {
    id: string
    minPrice?: number
    salePrice: number
    originalPrice?: number
}

export class PromotionHelper {
    public static fetchPromotions = async (merchantId: string, pageSize?: number, localMode?: boolean, appAccessToken?: string) => {
        const promotionParams: MerchantBenefits.SearchParams = {
            merchantId: merchantId,
            visibleLevel: localMode ? 'Offline' : 'Online'
        }

        if (pageSize) {
            promotionParams.pageSize = pageSize
        }

        const response = await CacheHandler.getPromotions(promotionParams, appAccessToken)

        return response
    }

    /** Lista dos produtos promocionais. */
    public static getProductsIds = (promotionResponse: MerchantBenefits.PromotionResponse) => {
        const ids: string[] = []

        if (!promotionResponse?.promotions) return ids

        promotionResponse.promotions.forEach((promo) => {
            promo.products?.forEach((product) => {
                ids.push(product.id)
            })
        })

        return ids
    }

    /** Obtém o desconto formatado. */
    public static getDiscount = (promotion: MerchantBenefits.PromotionResponse | undefined, item: Items.ItemResponse) => {
        let msg = ''

        if (promotion?.promotions) {
            for (const promo of promotion.promotions) {
                const product = promo.products?.find(i => i.id === item.id)

                if (product) {
                    switch (product.valueType) {
                        case 'Discount':
                            msg = `${product.value}%`
                            break
                        case 'Value':
                            msg = `${(item.salePrice - product.value).toLocalePrice()}`
                            break
                        case 'FreeItem':
                            msg = `Pague {itemValor - 1}, o {itemValor}º é grátis`
                            break
                        default:
                            break
                    }
                }
            }
        }

        return msg
    }

    /** Obtém o valor **De ---** **Por ---** do produto. */
    public static getFromToPrice = (promotion: MerchantBenefits.PromotionResponse | undefined, item: ItemProps): { from: number, to: number } => {
        const from = (item.salePrice ? item.salePrice : item.minPrice) ?? 0
        let to = (item.salePrice ? item.salePrice : item.minPrice) ?? 0

        if (promotion?.promotions) {
            for (const promo of promotion.promotions) {
                const product = promo.products?.find(i => i.id === item.id)

                if (product) {
                    // Quando abre o modal do produto, valida o valor original para aplicar o cálculo.
                    // Neste contexto o valor de venda já é com desconto.
                    const salePrice = item.originalPrice ?? item.salePrice
                    const price = (salePrice ? salePrice : item.minPrice) ?? 0

                    switch (product.valueType) {
                        case 'Discount':
                            const discount = (price * product.value) / 100
                            to = price - discount
                            break
                        case 'Value':
                            to = product.value
                            break
                        default:
                            break
                    }
                }
            }
        }

        return {
            from: from,
            to: to.priceRoundAwayFromZero()
        }
    }

    /** Obtém um produto da promoção. */
    public static getProduct = (promotion?: MerchantBenefits.PromotionResponse, productId?: string) => {
        if (!promotion?.promotions || !productId) return undefined

        for (const promo of promotion.promotions) {
            const product = promo.products?.find(i => i.id === productId)
            if (product) return product
        }

        return undefined
    }

    /** Obtém a promoção do produto. */
    public static getPromotionByProductId = (promotion?: MerchantBenefits.PromotionResponse, productId?: string) => {
        if (!promotion?.promotions || !productId) return undefined

        return promotion.promotions.find(promo =>
            promo.products?.some(pr => pr.id === productId)
        )
    }

    /** Texto formatado para exibição nos cards. */
    public static getDisplayLabel = (product: Items.ItemResponse, promotion?: MerchantBenefits.PromotionResponse) => {
        const promotionByProduct = this.getPromotionByProductId(promotion, product.id)
        const discount = this.getDiscount(promotion, product)
        return promotionByProduct ? `${promotionByProduct.name} - ${discount}` : ''
    }
}
